import { useEffect, useState, useCallback, useMemo, useRef } from "react";

import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Tabs,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { LayoutStandard, Pagination } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants, TodoConstants } from "constant";
import { useListHook } from "hooks";
import { TodoService } from "services";
import { displayClasses } from "styles";
import { AppUtils, UserUtils } from "utils";

import TodoDetail from "./detail/index";
import FilterSidebar from "./FilterSidebar";
import { createTodoGroupLabels } from "./helpers";
import { TodoItemData } from "./item";
import NewTodoModal from "./new/index";
import TodoGroupedList from "./TodoGroupedList";
import { useTodoFilters } from "./useTodoFilters";

const {
  REQUEST_TODO_LIST_ID,
  REQUEST_TODO_CREATE_ID,
  REQUEST_TODO_VIEW_ID,
  REQUEST_TODO_REPLACEMENT_AVAILABLE_ID,
} = ServiceConstants;

type TodoTabValue = "me" | "others" | "replacement";

const TodoListPage = () => {
  const { t } = useTranslation();
  const canEdit = UserUtils.hasAuthority(AuthorityConstants.ROLE_TODO_CUD);
  const [searchParams, setSearchParams] = useSearchParams();
  // Todo linked from elsewhere (e.g. dashboard); opened once the first list response arrives
  const pendingTodoId = useRef<number | undefined>(
    Number(searchParams.get(TodoConstants.TODO_ID_QUERY_KEY)) || undefined,
  );

  const todoListService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_LIST_ID],
  );
  const todoCreateService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_CREATE_ID],
  );
  const todoViewService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_VIEW_ID],
  );
  const todoReplacementAvailableService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_REPLACEMENT_AVAILABLE_ID],
  );

  // Pagination hook
  const { page, updatePageable, resetList, totalPages, refreshList, setPage } = useListHook();

  // Local state
  const [todos, setTodos] = useState<TodoItemData[]>([]);
  const [selectedTodo, setSelectedTodo] = useState<TodoItemData | null>(null);
  const [newTodoOpened, setNewTodoOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<TodoTabValue>("me");
  const [canViewReplacementTab, setCanViewReplacementTab] = useState(false);
  const [loading, setLoading] = useState(true);
  const todoGroupLabels = useMemo(() => createTodoGroupLabels(t), [t]);
  const shouldShowPagination = todos.length > 0 && totalPages > 1;

  // Filters hook
  const {
    searchQuery,
    setSearchQuery,
    dateFilter,
    setDateFilter,
    typeFilter,
    setTypeFilter,
    priorityFilter,
    setPriorityFilter,
    archiveFilter,
    setArchiveFilter,
    groupedTodos,
  } = useTodoFilters(todos, todoGroupLabels);

  // Debounce search query for API calls
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);
  const previousRequestKey = useRef<string>();

  const requestParams = useMemo<TodoService.TodoListParams>(() => {
    const params: TodoService.TodoListParams = {
      archive: archiveFilter,
      pageSize: 20,
    };

    if (activeTab === "me") {
      params.myTodo = true;
    } else if (activeTab === "others") {
      params.myTodo = false;
    } else if (activeTab === "replacement") {
      params.replacement = true;
    }

    if (dateFilter === "today") {
      params.dateFilter = "TODAY";
    } else if (dateFilter === "week") {
      params.dateFilter = "NEXT_7_DAYS";
    } else if (dateFilter === "absence") {
      params.absence = true;
    }

    if (typeFilter !== "all") {
      params.type = typeFilter;
    }

    if (priorityFilter === "urgent") {
      params.urgent = true;
    } else if (priorityFilter === "important") {
      params.important = true;
    }

    if (debouncedSearchQuery.trim()) {
      params.search = debouncedSearchQuery.trim();
    }

    return params;
  }, [
    activeTab,
    archiveFilter,
    dateFilter,
    typeFilter,
    priorityFilter,
    debouncedSearchQuery,
  ]);

  const requestKey = useMemo(
    () =>
      [
        activeTab,
        requestParams.archive ? "archive" : "active",
        requestParams.dateFilter ?? "all",
        requestParams.absence ? "absence" : "scheduled",
        requestParams.replacement ? "replacement" : "standard",
        requestParams.type ?? "all",
        requestParams.urgent ? "urgent" : "normal",
        requestParams.important ? "important" : "standard",
        requestParams.search ?? "",
      ].join("|"),
    [activeTab, requestParams],
  );

  useEffect(() => {
    TodoService.todoReplacementAvailable();
  }, []);

  // Fetch todos when page changes
  useEffect(() => {
    if (page === undefined || page === -1) {
      resetList();
      return;
    }

    const filtersChanged = previousRequestKey.current !== requestKey;
    if (filtersChanged && page !== 0) {
      refreshList();
      return;
    }

    previousRequestKey.current = requestKey;

    setLoading(true);
    setSelectedTodo(null);
    TodoService.todoList({ ...requestParams, page });
  }, [page, refreshList, requestKey, requestParams, resetList]);

  // Handle todo list response
  useEffect(() => {
    AppUtils.resolveServiceObject(todoListService, REQUEST_TODO_LIST_ID, (data) => {
      if (data?.content) {
        updatePageable(data);
        setTodos(data.content);
      } else if (Array.isArray(data)) {
        setTodos(data);
      } else {
        setTodos([]);
      }
      setLoading(false);

      if (pendingTodoId.current) {
        TodoService.todoOne(pendingTodoId.current);
        pendingTodoId.current = undefined;
        setSearchParams(
          (params) => {
            params.delete(TodoConstants.TODO_ID_QUERY_KEY);
            return params;
          },
          { replace: true },
        );
      }
    });
  }, [todoListService, updatePageable, setSearchParams]);

  // Handle create response - refresh list
  useEffect(() => {
    AppUtils.resolveServiceObject(todoCreateService, REQUEST_TODO_CREATE_ID, () => {
      setNewTodoOpened(false);
      refreshList();
    });
  }, [todoCreateService, refreshList]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      todoReplacementAvailableService,
      REQUEST_TODO_REPLACEMENT_AVAILABLE_ID,
      (value) => setCanViewReplacementTab(Boolean(value)),
      () => setCanViewReplacementTab(false),
    );
  }, [todoReplacementAvailableService]);

  useEffect(() => {
    if (activeTab === "replacement" && !canViewReplacementTab) {
      setActiveTab("me");
    }
  }, [activeTab, canViewReplacementTab]);

  // Handle single todo view response
  useEffect(() => {
    AppUtils.resolveServiceObject(todoViewService, REQUEST_TODO_VIEW_ID, (data) => {
      if (data) {
        setSelectedTodo(data);
      }
    });
  }, [todoViewService]);

  // Scroll to top when page changes
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      const scrollables = document.querySelectorAll('.mantine-ScrollArea-viewport');
      scrollables.forEach((el) => {
        if (el.scrollTop > 0) {
          el.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
    },
    [setPage],
  );

  const handleSelectTodo = useCallback((todo: TodoItemData) => {
    TodoService.todoOne(todo.id);
  }, []);

  const handleSaveTodo = useCallback(
    async (updatedTodo: TodoItemData) => {
      const updateData: TodoService.TodoCreateRequest = {
        title: updatedTodo.title,
        description: updatedTodo.description,
        type: updatedTodo.type,
        timeType: updatedTodo.timeType,
        completionType: updatedTodo.completionType,
        urgent: updatedTodo.urgent,
        important: updatedTodo.important,
        archive: updatedTodo.archive ?? false,
        recurring: updatedTodo.recurring,
        recurringType: updatedTodo.recurringType,
        dueDate: updatedTodo.dueDate,
        startTime: updatedTodo.startTime,
        endTime: updatedTodo.endTime,
        clientId: updatedTodo.client?.id ?? 0,
        users: updatedTodo.users?.map((u) => ({ userId: u.id })) ?? [],
      };

      await TodoService.todoUpdate(updatedTodo.id, updateData);
      refreshList();
      setSelectedTodo(null);
    },
    [refreshList],
  );

  const handleDeleteTodo = useCallback(
    async (id: number) => {
      await TodoService.todoDelete(id);
      setSelectedTodo(null);
      refreshList();
    },
    [refreshList],
  );

  const handleCancelEdit = useCallback(() => {
    setSelectedTodo(null);
  }, []);

  const handleCompleteTodo = useCallback(
    async (id: number) => {
      await TodoService.todoMarkCompleted(id);
      refreshList();
    },
    [refreshList],
  );

  return (
    <LayoutStandard title={AppUtils.composeTabTitle(t("header.todo"))} padding={0} authority={AuthorityConstants.ROLE_TODO_R}>
      <Flex h="calc(100vh - 56px)">
        <FilterSidebar
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          priorityFilter={priorityFilter}
          setPriorityFilter={setPriorityFilter}
          archiveFilter={archiveFilter}
          setArchiveFilter={setArchiveFilter}
        />

        <Box flex={2}>
          <Tabs value={activeTab} onChange={(value) => setActiveTab((value as TodoTabValue) ?? "me")} variant="unstyled">
            <Tabs.List grow>
              <Tabs.Tab value="me" className={displayClasses.tabList}>
                {t("todoList.tabs.mine")}
              </Tabs.Tab>
              <Tabs.Tab value="others" className={displayClasses.tabList}>
                {t("todoList.tabs.others")}
              </Tabs.Tab>
              {canViewReplacementTab && (
                <Tabs.Tab value="replacement" className={displayClasses.tabList}>
                  {t("todoList.tabs.replacement")}
                </Tabs.Tab>
              )}
            </Tabs.List>

            <Group mt="md" px="xl">
              <TextInput
                flex={1}
                placeholder={t("todoList.searchPlaceholder")}
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {canEdit && (
                <ActionIcon size="lg" color="green" onClick={() => setNewTodoOpened(true)}>
                  <IconPlus size={20} />
                </ActionIcon>
              )}
            </Group>

            <NewTodoModal opened={newTodoOpened} onClose={() => setNewTodoOpened(false)} />

            <Tabs.Panel value="me" pt="md">
              <TodoGroupedList
                groupedTodos={groupedTodos}
                loading={loading}
                emptyMessage={t("todoList.empty.mine")}
                height="calc(100vh - 260px)"
                onSelectTodo={handleSelectTodo}
                showCheckbox={canEdit}
                onCompleteTodo={handleCompleteTodo}
              />
              {shouldShowPagination && (
                <Pagination value={page} onChange={handlePageChange} total={totalPages} />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="others" pt="md">
              <TodoGroupedList
                groupedTodos={groupedTodos}
                loading={loading}
                emptyMessage={t("todoList.empty.others")}
                height="calc(100vh - 260px)"
                onSelectTodo={handleSelectTodo}
                showCheckbox={false}
              />
              {shouldShowPagination && (
                <Pagination value={page} onChange={handlePageChange} total={totalPages} />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="replacement" pt="md">
              <TodoGroupedList
                groupedTodos={groupedTodos}
                loading={loading}
                emptyMessage={t("todoList.empty.replacement")}
                height="calc(100vh - 260px)"
                onSelectTodo={handleSelectTodo}
                showCheckbox={canEdit}
                onCompleteTodo={handleCompleteTodo}
              />
              {shouldShowPagination && (
                <Pagination value={page} onChange={handlePageChange} total={totalPages} />
              )}
            </Tabs.Panel>
          </Tabs>
        </Box>

        <Box flex={1} style={{ borderLeft: "1px solid var(--mantine-color-gray-3)" }}>
          <TodoDetail
            todo={selectedTodo}
            onSave={handleSaveTodo}
            onDelete={handleDeleteTodo}
            onCancel={handleCancelEdit}
            readOnly={!canEdit}
          />
        </Box>
      </Flex>
    </LayoutStandard>
  );
};

export default TodoListPage;
